import { formatSupabaseError } from './formatSupabaseError';
import { normalizeEmail } from './emailUtils';
import { getDbSupabase, supabaseForms } from './supabase';

export type ElevatedDepartmentSlug = 'it' | 'management';

export type PortalUserProfile = {
  id: string;
  email: string;
  display_name: string | null;
  department_id: string;
  salesperson_codes?: string[];
  /** NAV Windows / Web Service user id (e.g. BTH000). */
  nav_id?: string | null;
  auth_id?: string | null;
  removed_at?: string | null;
  removed_by?: string | null;
  removed_reason?: string | null;
};

export function isElevatedDepartmentSlug(
  slug: string | null | undefined,
): slug is ElevatedDepartmentSlug {
  const value = (slug ?? '').trim().toLowerCase();
  return value === 'it' || value === 'management';
}

/** Current user's department slug from shared portal profile (null if missing). */
export async function fetchOwnPortalDepartmentSlug(
  email: string,
  authUserId: string,
): Promise<string | null> {
  // Prefer security-definer RPC on the primary CRM client — does not need Forms JWT.
  // (Forms-client department lookup used to fail on auto-login and clear elevatedDepartment.)
  try {
    const { data, error } = await getDbSupabase('crm').rpc('portal_actor_department_slug');
    if (!error) {
      const slug = typeof data === 'string' ? data.trim().toLowerCase() : '';
      if (slug) return slug;
    }
  } catch {
    /* fall through to direct lookup */
  }

  const normalized = normalizeEmail(email);
  const byAuth = await getDbSupabase('crm')
    .from('portal_user_profiles')
    .select('department_id, auth_id, email')
    .eq('auth_id', authUserId)
    .maybeSingle();

  if (byAuth.error && byAuth.error.code !== '42P01' && byAuth.error.code !== 'PGRST116') {
    if (byAuth.error.code === '42P01') return null;
  }
  if (byAuth.error?.code === '42P01') return null;

  let preferred = byAuth.data;
  if (!preferred) {
    const byEmail = await getDbSupabase('crm')
      .from('portal_user_profiles')
      .select('department_id, auth_id, email')
      .ilike('email', normalized)
      .maybeSingle();
    if (byEmail.error?.code === '42P01') return null;
    if (byEmail.error) throw new Error(formatSupabaseError(byEmail.error));
    preferred = byEmail.data;
  } else if (byAuth.error) {
    throw new Error(formatSupabaseError(byAuth.error));
  }

  if (!preferred?.department_id) return null;

  // Last resort: Forms schema via primary client (same JWT as CRM), not supabaseForms.
  const { data: dept, error: deptError } = await getDbSupabase('crm')
    .schema('application_form')
    .from('departments')
    .select('slug')
    .eq('id', preferred.department_id)
    .maybeSingle();
  if (deptError) {
    if (deptError.code === '42P01' || deptError.code === 'PGRST106') {
      // Cross-schema not exposed — try Forms client only if it already has a session.
      const { data: formsDept, error: formsError } = await supabaseForms
        .from('departments')
        .select('slug')
        .eq('id', preferred.department_id)
        .maybeSingle();
      if (formsError) {
        if (formsError.code === '42P01') return null;
        throw new Error(formatSupabaseError(formsError));
      }
      const formsSlug =
        typeof formsDept?.slug === 'string' ? formsDept.slug.trim().toLowerCase() : '';
      return formsSlug || null;
    }
    throw new Error(formatSupabaseError(deptError));
  }
  const slug = typeof dept?.slug === 'string' ? dept.slug.trim().toLowerCase() : '';
  return slug || null;
}

/** Current user's assigned NAV salesperson codes (empty = no Collection scope). */
export async function fetchOwnSalespersonCodes(): Promise<string[]> {
  const { data, error } = await getDbSupabase('crm').rpc('portal_actor_salesperson_codes');
  if (error) {
    if (/portal_actor_salesperson_codes|PGRST202|does not exist/i.test(error.message ?? '')) {
      return [];
    }
    throw new Error(formatSupabaseError(error));
  }
  return parseSalespersonCodes(data);
}

/** Own codes plus supervised team members' codes for Accounts Collection. */
export async function fetchCollectionSalespersonCodes(): Promise<string[]> {
  const { data, error } = await getDbSupabase('crm').rpc('portal_actor_collection_salesperson_codes');
  if (error) {
    if (/portal_actor_collection_salesperson_codes|PGRST202|does not exist/i.test(error.message ?? '')) {
      return fetchOwnSalespersonCodes();
    }
    throw new Error(formatSupabaseError(error));
  }
  return parseSalespersonCodes(data);
}

function parseSalespersonCodes(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return [...new Set(raw.map((code) => String(code ?? '').trim()).filter(Boolean))];
}

export async function fetchPortalUserProfiles(): Promise<PortalUserProfile[]> {
  const { data, error } = await getDbSupabase('crm')
    .from('portal_user_profiles')
    .select(
      'id, email, display_name, department_id, salesperson_codes, nav_id, auth_id, removed_at, removed_by, removed_reason',
    )
    .order('email');
  if (error) {
    if (error.code === '42P01') {
      throw new Error(
        'Table crm.portal_user_profiles not found. Run supabase/migrations/082_portal_user_profiles.sql in Supabase.',
      );
    }
    throw new Error(formatSupabaseError(error));
  }
  return (data ?? []).map((row) => ({
    id: String(row.id),
    email: normalizeEmail(String(row.email ?? '')),
    display_name: (row.display_name as string | null) ?? null,
    department_id: String(row.department_id ?? ''),
    salesperson_codes: parseSalespersonCodes(row.salesperson_codes),
    nav_id: (row.nav_id as string | null)?.trim() || null,
    auth_id: (row.auth_id as string | null) ?? null,
    removed_at: (row.removed_at as string | null) ?? null,
    removed_by: (row.removed_by as string | null) ?? null,
    removed_reason: (row.removed_reason as string | null) ?? null,
  }));
}

export async function upsertPortalUserProfile(input: {
  email: string;
  displayName?: string | null;
  departmentId: string;
  salespersonCodes?: string[] | null;
  /** Pass string to set/clear; omit to leave unchanged. */
  navId?: string | null;
}): Promise<void> {
  const { error } = await getDbSupabase('crm').rpc('upsert_portal_user_profile', {
    p_email: normalizeEmail(input.email),
    p_display_name: input.displayName?.trim() || null,
    p_department_id: input.departmentId,
    p_salesperson_codes:
      input.salespersonCodes === undefined
        ? null
        : [...new Set((input.salespersonCodes ?? []).map((c) => c.trim()).filter(Boolean))],
    p_nav_id: input.navId === undefined ? null : String(input.navId ?? '').trim(),
  });
  if (error) throw new Error(formatSupabaseError(error));
}

/** Soft-remove shared portal profile (keeps CRM accounts/contacts). */
export async function softRemovePortalUserProfile(
  email: string,
  reason?: string | null,
): Promise<void> {
  const { error } = await getDbSupabase('crm').rpc('soft_remove_portal_user_profile', {
    p_email: normalizeEmail(email),
    p_reason: reason?.trim() || null,
  });
  if (error) throw new Error(formatSupabaseError(error));
}

export async function restorePortalUserProfile(email: string): Promise<void> {
  const { error } = await getDbSupabase('crm').rpc('restore_portal_user_profile', {
    p_email: normalizeEmail(email),
  });
  if (error) throw new Error(formatSupabaseError(error));
}

/** @deprecated Prefer softRemovePortalUserProfile — hard delete is no longer used. */
export async function deletePortalUserProfile(email: string): Promise<void> {
  await softRemovePortalUserProfile(email, 'Removed via deletePortalUserProfile');
}
